/**
 * Pairing a shrine's loop with its pop strip (ADR-096 decision 4). The pack draws an obelisk as two strips, a "buff
 * available" loop and a "getting buff" one-shot, tall and shorter, with and without grass. The game pairs them by the
 * words of their names, not the whole name: one of the pack's names lacks the word "animation"
 * (`shrine-shorter-getting buff-no grass`), so two names pair when they hold the same words once "animation" is left
 * out and "available" is read as "getting". The rule is the game's, never Daedalus's.
 */

/**
 * A prop frame as a catalogue lists it: the tile and the name the pack gives it.
 */
export interface NamedFrame {
  readonly tileset: string;
  readonly tile: number;
  readonly name: string;
}

const NOISE = "animation";
const LOOP_WORD = "available";
const POP_WORD = "getting";
const REQUIRED: readonly string[] = ["shrine", "buff", LOOP_WORD];

/**
 * The words of a frame name: its runs of letters and digits in lower case, "animation" left out, sorted, so two names
 * compare whatever their order, case and separators.
 *
 * @param name The frame name.
 * @returns The words.
 */
function wordsOf(name: string): string[] {
  return name
    .toLowerCase()
    .split(/[^a-z0-9]+/)
    .filter((word) => word !== "" && word !== NOISE)
    .sort();
}

/**
 * Whether a frame name is a shrine's loop: it holds the words "shrine", "buff" and "available", and not "getting".
 *
 * @param name The frame name.
 * @returns True for a "buff available" obelisk.
 */
export function isShrineLoopName(name: string): boolean {
  const words = wordsOf(name);
  return REQUIRED.every((word) => words.includes(word)) && !words.includes(POP_WORD);
}

/**
 * The name of the pop strip a shrine's loop pairs with.
 *
 * @param placedName The name of the frame a shrine is placed on.
 * @param names The names to choose among, such as those of the placed frame's tileset.
 * @returns The first listed name holding the placed name's words with "getting" for "available", or undefined when
 *   the placed name is no shrine's loop or none fits.
 * @example
 * shrinePopName("shrine-shorter-buff available animation-no grass-295x311", ["shrine-shorter-getting buff-no grass-295x311"]);
 * // "shrine-shorter-getting buff-no grass-295x311"
 */
export function shrinePopName(placedName: string, names: readonly string[]): string | undefined {
  if (!isShrineLoopName(placedName)) {
    return undefined;
  }
  const wanted = wordsOf(placedName)
    .map((word) => (word === LOOP_WORD ? POP_WORD : word))
    .sort()
    .join(" ");
  return names.find((name) => wordsOf(name).join(" ") === wanted);
}
